<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import { useAlert } from 'dashboard/composables';
import WhatsappConnectionAPI from 'custom/api/whatsappConnection';
import { useLocoI18n } from 'custom/composables/useLocoI18n';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t } = useLocoI18n();

const TOGGLES = [
  { key: 'groups', label: 'GROUPS' },
  { key: 'auto_read', label: 'AUTO_READ' },
  {
    key: 'typing_indicator',
    label: 'TYPING_INDICATOR',
    capability: 'presence',
  },
  { key: 'number_check', label: 'NUMBER_CHECK' },
  { key: 'mark_online', label: 'MARK_ONLINE', capability: 'presence' },
  { key: 'reject_calls', label: 'REJECT_CALLS', capability: 'presence' },
];

const settings = reactive({});
const capabilities = ref([]);
const loaded = ref(false);
const isImporting = ref(false);

const visibleToggles = computed(() =>
  TOGGLES.filter(
    toggle =>
      !toggle.capability || capabilities.value.includes(toggle.capability)
  )
);
const supportsHistory = computed(() => capabilities.value.includes('history'));

const save = async changes => {
  try {
    const { data } = await WhatsappConnectionAPI.updateSettings(
      props.inbox.id,
      changes
    );
    Object.assign(settings, data.settings);
    useAlert(t('LOCO.WHATSAPP.OPTIONS.SAVED'));
  } catch (error) {
    useAlert(error.response?.data?.error || error.message);
  }
};

const saveHistoryDays = () => {
  save({ history_days: Number(settings.history_days) || 0 });
};

// History WhatsApp sent when the number was linked: the gateway keeps it for 30 days
const importHistory = async () => {
  isImporting.value = true;
  try {
    const { data } = await WhatsappConnectionAPI.importHistory(
      props.inbox.id,
      Number(settings.history_days)
    );
    useAlert(
      data.sent
        ? t('LOCO.WHATSAPP.OPTIONS.IMPORT_STARTED', data.sent)
        : t('LOCO.WHATSAPP.OPTIONS.IMPORT_NOTHING')
    );
  } catch (error) {
    useAlert(error.response?.data?.error || error.message);
  } finally {
    isImporting.value = false;
  }
};

onMounted(async () => {
  try {
    const { data } = await WhatsappConnectionAPI.status(props.inbox.id);
    Object.assign(settings, data.settings);
    capabilities.value = data.capabilities || [];
  } finally {
    loaded.value = true;
  }
});
</script>

<template>
  <section v-if="loaded" class="flex flex-col gap-4">
    <h2 class="text-heading-2 text-n-slate-12">
      {{ t('LOCO.WHATSAPP.OPTIONS.TITLE') }}
    </h2>
    <SettingsToggleSection
      v-for="toggle in visibleToggles"
      :key="toggle.key"
      v-model="settings[toggle.key]"
      :header="t(`LOCO.WHATSAPP.OPTIONS.${toggle.label}`)"
      :description="t(`LOCO.WHATSAPP.OPTIONS.${toggle.label}_DESC`)"
      @update:model-value="value => save({ [toggle.key]: value })"
    />
    <div v-if="supportsHistory" class="flex flex-wrap items-start gap-3">
      <Input
        v-model="settings.history_days"
        class="max-w-xs"
        type="number"
        min="0"
        max="365"
        :label="t('LOCO.WHATSAPP.OPTIONS.HISTORY_DAYS')"
        :message="t('LOCO.WHATSAPP.OPTIONS.HISTORY_DAYS_DESC')"
        @blur="saveHistoryDays"
      />
      <Button
        class="mt-6"
        sm
        faded
        slate
        icon="i-lucide-history"
        :label="t('LOCO.WHATSAPP.OPTIONS.IMPORT_NOW')"
        :disabled="!(Number(settings.history_days) > 0)"
        :is-loading="isImporting"
        @click="importHistory"
      />
    </div>
  </section>
</template>
